import React, { useMemo } from 'react';
import { Card, Statistic, Tag, Tooltip, Spin, Alert, Row, Col } from 'antd';
import { 
  LineChartOutlined, 
  InfoCircleOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined
} from '@ant-design/icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import './GraficaLineas.scss';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const GraficaLineas = ({ 
  grafica, 
  datos, 
  loading = false, 
  error = null,
  onRefresh,
  className = ''
}) => {
  if (loading) {
    return (
      <Card 
        className={`grafica-lineas ${className}`}
        title={
          <div className="grafica-lineas__header">
            <LineChartOutlined />
            <span>{grafica?.titulo || 'Cargando...'}</span>
          </div>
        }
      >
        <div className="grafica-lineas__loading">
          <Spin size="large" tip="Cargando datos..." />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card 
        className={`grafica-lineas ${className}`}
        title={
          <div className="grafica-lineas__header">
            <LineChartOutlined />
            <span>{grafica?.titulo || 'Error'}</span>
          </div>
        }
      >
        <Alert
          message="Error al cargar datos"
          description={error}
          type="error"
          showIcon
          action={
            onRefresh && (
              <Tooltip title="Reintentar">
                <ReloadOutlined 
                  onClick={() => onRefresh(grafica._id, grafica.tipoGrafica)}
                  style={{ cursor: 'pointer' }}
                />
              </Tooltip>
            )
          }
        />
      </Card>
    );
  }

  if (!datos || !datos.labels || !datos.datasets) {
    return (
      <Card 
        className={`grafica-lineas ${className}`}
        title={
          <div className="grafica-lineas__header">
            <LineChartOutlined />
            <span>{grafica?.titulo || 'Sin datos'}</span>
          </div>
        }
      >
        <div className="grafica-lineas__no-data">
          <InfoCircleOutlined />
          <p>No hay datos disponibles</p>
        </div>
      </Card>
    );
  }

  const { configuracion } = grafica;
  const { labels, datasets, estadisticas, rangoSensor } = datos;

  // Configurar datos del gráfico
  const chartData = useMemo(() => {
    const dataset = datasets[0];
    
    return {
      labels: labels.map(label => {
        const date = new Date(label);
        return date.toLocaleDateString('es-ES', { 
          month: 'short', 
          day: 'numeric' 
        });
      }),
      datasets: [
        {
          ...dataset,
          borderColor: configuracion?.colorLinea || dataset.borderColor || '#2196F3',
          backgroundColor: configuracion?.colorLinea 
            ? `${configuracion.colorLinea}20` 
            : dataset.backgroundColor || 'rgba(33, 150, 243, 0.1)',
          fill: true,
          tension: configuracion?.suavizado ? 0.4 : 0.1,
          pointRadius: configuracion?.mostrarPuntos ? 4 : 2,
          pointHoverRadius: 6,
          pointBackgroundColor: configuracion?.colorLinea || dataset.borderColor || '#2196F3',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
        }
      ]
    };
  }, [labels, datasets, configuracion]);

  // Configurar opciones del gráfico
  const chartOptions = useMemo(() => {
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: configuracion?.colorLinea || '#2196F3',
          borderWidth: 1,
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${context.parsed.y.toFixed(1)} ${rangoSensor?.unidades || ''}`;
            }
          }
        }
      },
      scales: {
        x: {
          display: true,
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.1)'
          },
          ticks: {
            maxTicksLimit: 10,
            color: '#666'
          }
        },
        y: {
          display: true,
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.1)'
          },
          ticks: {
            color: '#666',
            callback: function(value) {
              return `${value} ${rangoSensor?.unidades || ''}`;
            }
          },
          min: rangoSensor?.min,
          max: rangoSensor?.max
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      },
      elements: {
        point: {
          hoverRadius: 8
        }
      }
    };

    // Agregar línea de promedio si está habilitada
    if (configuracion?.mostrarPromedio && estadisticas?.promedio) {
      options.plugins.annotation = {
        annotations: {
          promedio: {
            type: 'line',
            yMin: estadisticas.promedio,
            yMax: estadisticas.promedio,
            borderColor: '#ff6b6b',
            borderWidth: 2,
            borderDash: [5, 5],
            label: {
              content: `Promedio: ${estadisticas.promedio.toFixed(1)} ${rangoSensor?.unidades || ''}`,
              enabled: true,
              position: 'end'
            }
          }
        }
      };
    }

    return options;
  }, [configuracion, estadisticas, rangoSensor]);

  const getTendenciaIcon = (tendencia) => {
    switch (tendencia) {
      case 'ascendente':
        return <ArrowUpOutlined style={{ color: '#52c41a' }} />;
      case 'descendente':
        return <ArrowDownOutlined style={{ color: '#ff4d4f' }} />;
      case 'estable':
      default:
        return <MinusOutlined style={{ color: '#faad14' }} />;
    }
  };

  const getTendenciaColor = (tendencia) => {
    switch (tendencia) {
      case 'ascendente':
        return 'green';
      case 'descendente':
        return 'red';
      case 'estable':
      default:
        return 'orange';
    }
  };

  const formatValue = (value) => {
    if (typeof value !== 'number') return 'N/A';
    return value.toFixed(1);
  };

  return (
    <Card 
      className={`grafica-lineas ${className}`}
      title={
        <div className="grafica-lineas__header">
          <LineChartOutlined />
          <span>{grafica.titulo}</span>
          {configuracion?.mostrarTendencia && estadisticas?.tendencia && (
            <div className="grafica-lineas__tendencia">
              {getTendenciaIcon(estadisticas.tendencia)}
            </div>
          )}
          {onRefresh && (
            <Tooltip title="Actualizar datos">
              <ReloadOutlined 
                onClick={() => onRefresh(grafica._id, grafica.tipoGrafica)}
                className="grafica-lineas__refresh"
              />
            </Tooltip>
          )}
        </div>
      }
      extra={
        configuracion?.mostrarTendencia && estadisticas?.tendencia && (
          <Tag color={getTendenciaColor(estadisticas.tendencia)}>
            {estadisticas.tendencia.toUpperCase()}
          </Tag>
        )
      }
    >
      {grafica.descripcion && (
        <div className="grafica-lineas__description">
          <p>{grafica.descripcion}</p>
        </div>
      )}

      <div className="grafica-lineas__content">
        {/* Gráfico principal */}
        <div className="grafica-lineas__chart">
          <Line data={chartData} options={chartOptions} />
        </div>

        {/* Estadísticas */}
        {estadisticas && (
          <div className="grafica-lineas__stats">
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Promedio"
                  value={formatValue(estadisticas.promedio)}
                  suffix={rangoSensor?.unidades}
                  valueStyle={{ fontSize: '1.2em', color: '#1890ff' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Mínimo"
                  value={formatValue(estadisticas.minimo)}
                  suffix={rangoSensor?.unidades}
                  valueStyle={{ fontSize: '1.2em', color: '#52c41a' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Máximo"
                  value={formatValue(estadisticas.maximo)}
                  suffix={rangoSensor?.unidades}
                  valueStyle={{ fontSize: '1.2em', color: '#ff4d4f' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <div className="grafica-lineas__periodo">
                  <div className="grafica-lineas__periodo-title">Período</div>
                  <div className="grafica-lineas__periodo-value">
                    {configuracion?.periodoHistorico || 30} días
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>

      <div className="grafica-lineas__footer">
        <small>
          Datos del {labels[0]} al {labels[labels.length - 1]} • {labels.length} puntos de datos
        </small>
      </div>
    </Card>
  );
};

export default GraficaLineas;